// Phase 6.1 — /users management page (superadmin only).
// Phase 8.2 redesign — Apple-style card surface:
//   * each user is a rounded "bubble" card with a gradient avatar,
//     name/email, a role pill, and pill-shaped actions
//   * Reset password opens a POPUP (no inline fields in the dropdown)
//   * expanding a card reveals only that user's owned workspaces, with
//     per-archive hard-delete
//   * a SINGLE refresh in the header reloads both the user list and the
//     pending-workspaces queue
//
// Every write is step-up authed via <StepUpAuthModal> — the superadmin
// re-enters their own password. The server re-checks role + password on
// each call; this page is the operator UI on top of those endpoints.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  KeyRound,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";

import { HomeButton } from "../components/HomeButton";
import { SignOutButton } from "../components/SignOutButton";

import {
  ApiError,
  changeOwnPassword,
  deleteUser,
  deleteUserWorkspace,
  listManagedUsers,
  listUserWorkspaces,
  resetUserPassword,
  type CreateUserResponse,
  type ManagedUser,
  type UserWorkspaceEntry,
} from "../lib/api";
import { useAuth } from "../state/AuthContext";
import { formatAbsoluteIst, formatRelative } from "../lib/timeFmt";
import { StepUpAuthModal } from "../components/StepUpAuthModal";
import { AddUserModal } from "../components/AddUserModal";
import { DeleteWorkspaceDialog } from "../components/DeleteWorkspaceDialog";
import { PendingWorkspaces } from "../components/PendingWorkspaces";
import { Modal } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { EmptyState } from "../components/ui/EmptyState";

// ---------- Apple-ish bits ----------------------------------------

// A small set of soft gradient pairs; pick deterministically from the
// username so a user's avatar bubble is stable across renders.
const AVATAR_GRADIENTS = [
  "from-[#5b8def] to-[#7c5cff]",
  "from-[#ff7a59] to-[#ff4d6d]",
  "from-[#22c1c3] to-[#2d9cdb]",
  "from-[#f6a623] to-[#f857a6]",
  "from-[#11998e] to-[#38ef7d]",
  "from-[#8e2de2] to-[#4a00e0]",
];

function gradientFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length]!;
}

function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  return (
    <div
      aria-hidden
      className={`${dim} flex-shrink-0 rounded-full bg-gradient-to-br ${gradientFor(name)} text-white font-semibold flex items-center justify-center shadow-sm`}
    >
      {(name[0] ?? "?").toUpperCase()}
    </div>
  );
}

// Pill button — rounded-full, soft. tone: neutral | primary | danger.
function Pill({
  children,
  tone = "neutral",
  className = "",
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: "neutral" | "primary" | "danger";
}) {
  const toneCls =
    tone === "danger"
      ? "text-theme-error-text border-theme-error-border/60 hover:bg-theme-error-bg"
      : tone === "primary"
        ? "text-white bg-theme-accent-link border-transparent hover:opacity-90"
        : "text-theme-text-secondary border-theme-border-default hover:text-theme-text-primary hover:border-theme-border-emphasis";
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[11px] font-medium tracking-wide transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${toneCls} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

const ROLE_PILL: Record<string, string> = {
  superadmin: "bg-theme-accent-bg/40 text-theme-accent-link",
  user: "bg-theme-bg-chip-subtle text-theme-text-secondary",
};

// ---------- page --------------------------------------------------

export function UsersPage() {
  // Role gate. The server is authoritative; this just spares a
  // non-superadmin the 403 round-trip. useAuth runs unconditionally.
  const { role } = useAuth();
  if (role !== "superadmin") {
    return <Navigate to="/chooser" replace />;
  }
  return <UsersPageInner />;
}

function UsersPageInner() {
  const { userId } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  // Bumping this re-mounts/reloads the PendingWorkspaces panel so the
  // single header refresh reloads the whole page in one click.
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const r = await listManagedUsers(signal);
      if (signal?.aborted) return;
      setUsers(r.users);
      setError(null);
    } catch (e) {
      if (signal?.aborted) return;
      if (e instanceof ApiError) setError(e);
      else setError(new ApiError(String(e), 0));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    setReloadKey((k) => k + 1); // reloads PendingWorkspaces too
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const onCreated = (created: CreateUserResponse) => {
    setShowAddUser(false);
    void load();
    setReloadKey((k) => k + 1);
    toast({ title: `User ${created.username} created`, type: "success" });
  };

  const onDeleted = (username: string) => {
    setExpandedId(null);
    void load();
    toast({ title: `User ${username} deleted`, type: "success" });
  };

  return (
    <div data-testid="users-page" className="min-h-screen text-theme-text-primary">
      <header className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 border-b border-theme-border-subtle bg-theme-bg-app/80 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <HomeButton />
          <h1 data-testid="users-title" className="text-h2 font-semibold">
            User &amp; workspace management
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Phase 8.2: the ONE refresh — reloads users + pending. */}
          <Pill
            onClick={onRefresh}
            disabled={refreshing || loading}
            data-testid="users-refresh"
            aria-label="Refresh"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
              strokeWidth={2}
              aria-hidden
            />
            Refresh
          </Pill>
          <Pill
            tone="primary"
            onClick={() => setShowAddUser(true)}
            data-testid="users-add"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            Add user
          </Pill>
          <SignOutButton />
        </div>
      </header>

      <main className="px-6 py-8 max-w-3xl mx-auto space-y-6">
        <PendingWorkspaces
          reloadKey={reloadKey}
          onUsersChanged={() => void load()}
        />

        {error && (
          <div
            data-testid="users-error"
            className="rounded-2xl bg-theme-error-bg border border-theme-error-border text-theme-error-text px-5 py-4 text-sm"
          >
            Failed to load users: {error.message} (HTTP {error.status})
          </div>
        )}

        {loading ? (
          <LoadingSkeleton />
        ) : users && users.length === 0 ? (
          <div
            data-testid="users-empty"
            className="rounded-2xl glass-card"
          >
            <EmptyState
              icon={<Users className="w-full h-full" strokeWidth={1.5} />}
              title="No team members yet"
              description="Add a user to start onboarding teammates."
              action={{ label: "Add user", onClick: () => setShowAddUser(true) }}
            />
          </div>
        ) : users ? (
          <>
          <h2 className="text-h3 font-semibold -mb-2">Team members</h2>
          <ul data-testid="users-table" className="space-y-2.5">
            {users.map((u) => (
              <UserCard
                key={u.user_id}
                user={u}
                isSelf={u.user_id === userId}
                expanded={expandedId === u.user_id}
                onToggle={() =>
                  setExpandedId((cur) => (cur === u.user_id ? null : u.user_id))
                }
                onDeleted={onDeleted}
              />
            ))}
          </ul>
          </>
        ) : null}
      </main>

      {showAddUser && (
        <AddUserModal onCreated={onCreated} onClose={() => setShowAddUser(false)} />
      )}
    </div>
  );
}

// ---------- user card ---------------------------------------------

interface UserCardProps {
  user: ManagedUser;
  isSelf: boolean;
  expanded: boolean;
  onToggle: () => void;
  onDeleted: (username: string) => void;
}

function UserCard({ user, isSelf, expanded, onToggle, onDeleted }: UserCardProps) {
  const [showDelete, setShowDelete] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const rolePill = ROLE_PILL[user.role] ?? ROLE_PILL.user;

  const onDeleteConfirm = useCallback(
    async (currentPassword: string) => {
      await deleteUser(user.user_id, currentPassword);
      setShowDelete(false);
      onDeleted(user.username);
    },
    [user.user_id, user.username, onDeleted],
  );

  return (
    <li
      data-testid={`users-row-${user.user_id}`}
      className="rounded-2xl glass-card overflow-hidden transition-shadow duration-150 hover:shadow-[0_1px_3px_rgba(0,0,0,0.18)]"
    >
      {/* Header row — click anywhere (except buttons) to expand. */}
      <div
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
      >
        <Avatar name={user.username} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-sans text-[15px] font-medium text-theme-text-primary truncate">
              {user.username}
            </span>
            {isSelf && (
              <span
                data-testid={`users-self-${user.user_id}`}
                className="text-[10px] text-theme-text-tertiary"
              >
                you
              </span>
            )}
            <span
              data-testid={`users-role-${user.user_id}`}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${rolePill}`}
            >
              {user.role}
            </span>
          </div>
          <div className="font-mono text-xs text-theme-text-muted truncate">
            {user.email ?? "—"}
            <span className="text-theme-text-tertiary">
              {" · joined "}
              <span title={formatAbsoluteIst(user.created_at)}>
                {formatRelative(user.created_at)}
              </span>
            </span>
          </div>
        </div>

        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Pill
            onClick={() => setShowReset(true)}
            data-testid={`users-reset-toggle-${user.user_id}`}
          >
            <KeyRound className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">Reset password</span>
          </Pill>
          <Pill
            tone="danger"
            onClick={() => {
              if (!isSelf) setShowDelete(true);
            }}
            disabled={isSelf}
            data-testid={`users-delete-${user.user_id}`}
            title={isSelf ? "You cannot delete your own account" : "Delete user"}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">Delete</span>
          </Pill>
          <span className="text-theme-text-tertiary pl-0.5">
            {expanded ? (
              <ChevronDown className="w-4 h-4" strokeWidth={2} aria-hidden />
            ) : (
              <ChevronRight className="w-4 h-4" strokeWidth={2} aria-hidden />
            )}
          </span>
        </div>
      </div>

      {/* Expanded: ONLY the owned-workspaces panel now (password reset
          moved to its own popup). */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-theme-border-subtle">
          <UserWorkspacesPanel userId={user.user_id} username={user.username} />
        </div>
      )}

      {showReset && (
        <ResetPasswordModal
          user={user}
          isSelf={isSelf}
          onClose={() => setShowReset(false)}
        />
      )}
      {showDelete && (
        <StepUpAuthModal
          title="Delete user?"
          description={
            <>
              This permanently removes{" "}
              <span className="text-theme-text-primary">{user.username}</span>{" "}
              and all of their workspaces, messages, attachments, and Drive
              data. This cannot be undone. Enter your own password to authorize.
            </>
          }
          confirmLabel="Delete forever"
          confirmTone="danger"
          onConfirm={onDeleteConfirm}
          onClose={() => setShowDelete(false)}
        />
      )}
    </li>
  );
}

// ---------- reset-password popup ----------------------------------

const PASSWORD_HINT = "At least 8 characters.";

function ResetPasswordModal({
  user,
  isSelf,
  onClose,
}: {
  user: ManagedUser;
  isSelf: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showStepUp, setShowStepUp] = useState(false);

  const clientError = useMemo(() => {
    if (newPassword && newPassword.length < 8) return PASSWORD_HINT;
    if (confirm && newPassword !== confirm) return "Passwords do not match.";
    return null;
  }, [newPassword, confirm]);

  const canSubmit =
    newPassword.length >= 8 && confirm.length > 0 && clientError === null;

  const onConfirm = useCallback(
    async (currentPassword: string) => {
      if (isSelf) {
        await changeOwnPassword(currentPassword, newPassword);
      } else {
        await resetUserPassword(user.user_id, currentPassword, newPassword);
      }
      setShowStepUp(false);
      toast({
        title: isSelf
          ? "Your password was updated"
          : `Password updated for ${user.username}`,
        type: "success",
      });
      onClose();
    },
    [isSelf, user.user_id, user.username, newPassword, toast, onClose],
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isSelf ? "Reset your password" : `Reset ${user.username}'s password`}
      size="sm"
    >
      <form
        data-testid={`users-reset-editor-${user.user_id}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) setShowStepUp(true);
        }}
        className="space-y-4"
      >
        <div className="flex items-center gap-3">
          <Avatar name={user.username} size="sm" />
          <div className="min-w-0">
            <div className="text-sm text-theme-text-primary truncate">
              {user.username}
            </div>
            <div className="font-mono text-xs text-theme-text-muted truncate">
              {user.email ?? "—"}
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor={`reset-new-${user.user_id}`}
            className="block text-[11px] font-medium text-theme-text-tertiary mb-1.5"
          >
            New password
          </label>
          <input
            id={`reset-new-${user.user_id}`}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            data-testid={`users-reset-new-${user.user_id}`}
            className="w-full px-3.5 py-2.5 rounded-xl bg-theme-bg-app border border-theme-border-default text-sm text-theme-text-primary focus:outline-none focus:border-theme-border-active"
          />
        </div>
        <div>
          <label
            htmlFor={`reset-confirm-${user.user_id}`}
            className="block text-[11px] font-medium text-theme-text-tertiary mb-1.5"
          >
            Confirm
          </label>
          <input
            id={`reset-confirm-${user.user_id}`}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            aria-invalid={clientError ? "true" : "false"}
            data-testid={`users-reset-confirm-${user.user_id}`}
            className={[
              "w-full px-3.5 py-2.5 rounded-xl bg-theme-bg-app border text-sm",
              "text-theme-text-primary focus:outline-none",
              clientError
                ? "border-theme-error-border focus:border-theme-error-border"
                : "border-theme-border-default focus:border-theme-border-active",
            ].join(" ")}
          />
        </div>

        {clientError && (
          <p
            data-testid={`users-reset-error-${user.user_id}`}
            className="text-[11px] text-theme-error-text"
          >
            {clientError}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-[12px] text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid={`users-reset-submit-${user.user_id}`}
            className="px-4 py-2 rounded-full bg-theme-accent-link text-white text-[12px] font-medium hover:opacity-90 transition-opacity duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Update password
          </button>
        </div>
      </form>

      {showStepUp && (
        <StepUpAuthModal
          title="Confirm with your password"
          description={
            isSelf ? (
              <>Enter your current password to set a new one.</>
            ) : (
              <>
                Resetting the password for{" "}
                <span className="text-theme-text-primary">{user.username}</span>.
                Enter your own password to authorize.
              </>
            )
          }
          confirmLabel="Update password"
          onConfirm={onConfirm}
          onClose={() => setShowStepUp(false)}
        />
      )}
    </Modal>
  );
}

// ---------- owned-workspaces panel --------------------------------

function UserWorkspacesPanel({
  userId,
  username,
}: {
  userId: string;
  username: string;
}) {
  const [workspaces, setWorkspaces] = useState<UserWorkspaceEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<UserWorkspaceEntry | null>(null);
  const { toast } = useToast();

  const load = useCallback(() => {
    setError(null);
    listUserWorkspaces(userId)
      .then((r) => setWorkspaces(r.workspaces))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "failed to load workspaces"),
      );
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid={`user-workspaces-${userId}`} className="mt-3">
      <h4 className="text-[11px] uppercase tracking-widest text-theme-text-tertiary mb-2">
        Workspaces owned by {username}
      </h4>
      {error && <p className="text-xs text-theme-error-text">{error}</p>}
      {!error && workspaces === null && (
        <p className="text-xs text-theme-text-muted">Loading…</p>
      )}
      {!error && workspaces !== null && workspaces.length === 0 && (
        <p className="text-xs text-theme-text-muted">
          No workspaces archived by this user.
        </p>
      )}
      {!error && workspaces !== null && workspaces.length > 0 && (
        <ul className="space-y-2">
          {workspaces.map((w) => (
            <li
              key={w.team_id}
              data-testid={`user-workspace-row-${userId}-${w.team_id}`}
              className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-theme-bg-app border border-theme-border-subtle"
            >
              <div className="flex-1 min-w-0">
                <span className="font-sans text-sm text-theme-text-primary">
                  {w.team_name ?? w.team_id}
                </span>
                <span className="font-mono text-[10px] text-theme-text-muted ml-2">
                  {w.team_id} · {w.message_count} msgs · {w.attachment_count} atts
                  {w.backfill_complete ? "" : " · backfill in progress"}
                </span>
              </div>
              <Pill
                tone="danger"
                onClick={() => setConfirming(w)}
                data-testid={`user-workspace-delete-${userId}-${w.team_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                Delete
              </Pill>
            </li>
          ))}
        </ul>
      )}

      {confirming && (
        <DeleteWorkspaceDialog
          workspaceName={confirming.team_name ?? confirming.team_id}
          conversationCount={null}
          onClose={() => setConfirming(null)}
          onConfirm={async () => {
            await deleteUserWorkspace(userId, confirming.team_id);
            setConfirming(null);
            toast({
              title: `Deleted ${confirming.team_name ?? confirming.team_id} for ${username}`,
              type: "success",
            });
            load();
          }}
        />
      )}
    </div>
  );
}

// ---------- loading skeleton --------------------------------------

function LoadingSkeleton() {
  return (
    <ul data-testid="users-loading" className="space-y-2.5">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 px-4 py-3 rounded-2xl glass-card"
        >
          <div className="w-10 h-10 rounded-full bg-theme-bg-chip-subtle animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 rounded bg-theme-bg-chip-subtle animate-pulse" />
            <div className="h-2.5 w-48 rounded bg-theme-bg-chip-subtle animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}
